import { useMemo } from 'react';
import { useUsers } from './useUsers';

/**
 * Profile photos, looked up wherever a teammate is drawn.
 *
 * Most places that render an avatar only have a display name (task assignee,
 * comment author, calendar chip, meet tile…), and threading `avatarUrl`
 * through every API and prop would touch dozens of endpoints. Instead the
 * team list (`GET /api/users`, cached 10 min) doubles as a directory: by user
 * id when the caller has one, else by normalized name. The lists are small
 * (a firm's team), so the maps are cheap and rebuilt only when the users
 * query changes.
 */

export function normalizeName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export interface AvatarDirectory {
  /** The photo for a user id or, failing that, a display name; null when unknown. */
  lookup: (userId?: string | null, name?: string | null) => string | null;
}

export function useAvatarDirectory(): AvatarDirectory {
  const { data } = useUsers();
  return useMemo(() => {
    const byId = new Map<string, string>();
    const byName = new Map<string, string>();
    for (const u of data ?? []) {
      if (!u.avatarUrl) continue;
      byId.set(u.id, u.avatarUrl);
      const key = normalizeName(u.name);
      if (key && !byName.has(key)) byName.set(key, u.avatarUrl);
    }
    return {
      lookup: (userId, name) =>
        (userId ? byId.get(userId) : undefined) ??
        (name ? byName.get(normalizeName(name)) : undefined) ??
        null,
    };
  }, [data]);
}

/** One avatar's photo: an explicit `src` wins, then the directory by id, then by name. */
export function useAvatarSrc(opts: {
  src?: string | null;
  userId?: string | null;
  name?: string | null;
}): string | null {
  const directory = useAvatarDirectory();
  return opts.src ?? directory.lookup(opts.userId, opts.name);
}
